/**
 * @file Cloudflare image resizing support.
 */
import { TransformOptions } from './types'

const MAX_WIDTH = 1600
const MAX_HEIGHT = 1600

function mapGravity(vivaldiGravity?: string): string {
  switch (vivaldiGravity) {
    case 'north':
      return 'top'
    case 'south':
      return 'bottom'
    case 'west':
      return 'left'
    case 'east':
      return 'right'
    case 'center':
      return '0.5x0.5'
    case 'north_east':
      return '1x0'
    case 'north_west':
      return '0x0'
    case 'south_east':
      return '1x1'
    case 'south_west':
      return '0x1'
    default:
      return 'auto'
  }
}

function mapCropToFit(vivaldiCrop: string): string {
  switch (vivaldiCrop) {
    case 'limit':
      return 'scale-down'
    case 'lfill':
      return 'crop'
    case 'fill':
      return 'cover'
    case 'scale':
      return 'contain'
    default:
      return 'scale-down'
  }
}

function getDimensions(options: TransformOptions): { width: number | undefined; height: number | undefined } {
  const dpr = options.dpr ? Number(options.dpr) : 1
  let width = options.width ? Math.floor(options.width * dpr) : undefined
  let height = options.height ? Math.floor(options.height * dpr) : undefined
  if (!width && options.height && options.ar) {
    width = Math.floor(options.ar * options.height)
  }
  if (!height && options.width && options.ar) {
    height = Math.floor(options.width / options.ar)
  }
  return { width, height }
}

function getTransformParams(options: TransformOptions): string[] {
  const params: string[] = []
  const dimensions = getDimensions(options)

  if (dimensions.width !== undefined) {
    params.push(`width=${dimensions.width}`)
  }
  if (dimensions.height !== undefined) {
    params.push(`height=${dimensions.height}`)
  }
  if (options.quality !== undefined) {
    params.push(`quality=${options.quality}`)
  }
  if (options.format === undefined || options.format === 'auto' || options.format === 'webp') {
    params.push(`format=auto`)
  }
  if (options.blur !== undefined) {
    params.push(`blur=${options.blur}`)
  }
  if (options.trim !== undefined && options.trim) {
    params.push(`trim=auto`)
  }
  if (options.crop !== undefined) {
    params.push(`fit=${mapCropToFit(options.crop)}`)
  }

  // Handle gravity
  params.push(`gravity=${mapGravity(options.gravity)}`)

  return params
}

function getUrlPrefix(params: string[]): string {
  if (params.length === 0) {
    return ''
  }
  const sortedParams = [...params].sort()
  const imagePath = '/cdn-cgi/image/' + sortedParams.join(',')
  return imagePath
}

function processedUrl(url: string, options: TransformOptions): string {
  const parsedUrl = new URL(url)
  const params = getTransformParams(options)
  const imagePath = getUrlPrefix(params)
  const newPathname = imagePath + parsedUrl.pathname
  return `${parsedUrl.origin}${newPathname}${parsedUrl.search}`
}

function areOptionsSupported(options: TransformOptions): boolean {
  if (options.grayscale) return false
  if (options.effect) return false
  if (options.metadata) return false
  if (options.trim) return false
  const dimensions = getDimensions(options)
  if (!dimensions.width || !dimensions.height) return false
  if (dimensions.width > MAX_WIDTH || dimensions.height > MAX_HEIGHT) return false
  return true
}

function isUrlSupported(url: string): boolean {
  const parsedUrl = new URL(url)
  const isCorrectHost = parsedUrl.hostname === 'padlet.net'
  const isAcceptablePath =
    !parsedUrl.pathname.startsWith('/monsters/') &&
    !parsedUrl.pathname.startsWith('/homes/') &&
    !parsedUrl.pathname.startsWith('/identicons/')
  return isCorrectHost && isAcceptablePath
}

function canServe(url: string, options: TransformOptions): boolean {
  return isUrlSupported(url) && areOptionsSupported(options)
}

export { canServe, processedUrl }
